import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import { Icon } from '@/components/Icon';
import * as constants from '../constants';
import { injectFieldContext, useFieldContextValue } from '../context';
import { Input } from './Control';
/** @returns A typed select control component. */
export function createTypedSelect() {
    return tsx.withHtmlAttrs(tsx.componentFactoryOf().create({
        name: constants.nameSelect,
        inject: injectFieldContext(),
        props: {
            options: p.ofRoArray().required,
            value: p.ofType().optional,
            textStyle: p.ofType().default('body-medium'),
            disabled: p(Boolean).default(false),
            readonly: p(Boolean).default(false),
            selectClass: p.ofType().optional,
        },
        computed: {
            /** @returns True if `this.value` is not in the list from `this.options`. */
            valuePropNotWithinOptions() {
                return this.options.every(opt => opt.value !== this.value);
            },
            /** @returns The options rendered as <option> elements. */
            optionsNode() {
                const nodesFromOptionsProp = this.options.map(o => (<option key={o.value} value={o.value} selected={o.value === this.value} disabled={o.disabled}>
                            {o.label}
                        </option>));
                if (this.valuePropNotWithinOptions) {
                    // this.value is not in the provided options.
                    // Add a temporary, disabled <option> for it.
                    // This will be gone once the user select another option.
                    return [
                        <option key={`temp-${this.value}`} value={this.value} selected disabled>
                                {this.value}
                            </option>,
                        ...nodesFromOptionsProp,
                    ];
                }
                return nodesFromOptionsProp;
            },
            /** @returns The select and the chevron icon. */
            selectNode() {
                return [
                    <select id={this.fieldContext.inputId} aria-describedby={this.fieldContext.descriptionId} class={[
                            constants.nameControl,
                            constants.nameSelect,
                            'transition duration-100',
                            'w-full',
                            'disabled:cursor-not-allowed',
                            'disabled:text-neutral-60',
                            'disabled:bg-neutral-5 disabled:dark:bg-neutral-70',
                            'hover:bg-actionable-2 dark:hover:bg-actionable-60',
                            {
                                'italic text-neutral-50': this.valuePropNotWithinOptions,
                            },
                            this.selectClass,
                        ]} value={this.value} disabled={this.disabled} aria-disabled={this.disabled} onInput={evt => tsx.emitOn(this, 'onInput', evt.target.value)}>
                            {this.optionsNode}
                        </select>,
                    <Icon class={[
                            'absolute top-1/2 right-3 size-5 -translate-y-2.5 pointer-events-none',
                            {
                                'text-neutral-50 dark:text-neutral-20': this.disabled,
                            },
                        ]} icon={icons.ChevronDown}/>,
                ];
            },
            /** @returns The label for the currently selected value. */
            selectedLabel() {
                return (this.options.find(opt => opt.value === this.value)?.label ??
                    this.value ??
                    '');
            },
            /** @returns The read-only input node. */
            readonlyNode() {
                return <Input value={this.selectedLabel} readOnly class="w-full"/>;
            },
            ...useFieldContextValue(),
        },
        /** @returns Node of the component. */
        render() {
            return (<div class={`${constants.nameSelect}-container`} data-value={this.value} data-disabled={this.disabled}>
                        {this.readonly ? this.readonlyNode : this.selectNode}
                    </div>);
        },
    }));
}
/** A ready-made select control component that just accepts any string. */
export const Select = createTypedSelect();
